import { useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { Notice, PipelineMini, StatusPill } from "../../design/components";
import { usePortalDocuments, usePortalStageCounts, type PortalDocumentFilters, type PortalDocumentRow } from "../../api/portal";
import {
  INBOUND_STAGES,
  IN_STAGE_LABEL,
  OUTBOUND_STAGES,
  stageIndex,
  type InboundStage,
  type OutboundStage,
} from "../../domain/stages";
import { omr, relative } from "../../lib/format";
import { DocTypeCell } from "../documents/DocTypeCell";
import { FilterControls } from "../documents/FilterControls";
import { stageLabel } from "../documents/StageStrip";
import {
  isQueueFiltered,
  mergeQueueFilters,
  readQueueFilters,
  writeQueueFilters,
} from "../documents/queueFilterState";
import { documentName } from "./usePortal";
import styles from "./portal.module.css";

const PAGE_SIZE = 50;

/** Both vocabularies of m4-rulings.md #50, as on the console queue (DocumentRow.tsx pulls console hooks). */
const STATE_PILL: Record<string, { status: string; label: string }> = {
  in_flight: { status: "active", label: "In flight" },
  done: { status: "completed", label: "Completed" },
  error: { status: "error", label: "Error" },
  held: { status: "held", label: "Held" },
  landed: { status: "completed", label: "Landed" },
};

export function statePill(state: string): { status: string; label: string } {
  return STATE_PILL[state] ?? { status: state, label: state };
}

export const portalQueue = (stage: string) => `/portal/documents?stage=${stage}`;

function isOutboundStage(stage: string): stage is OutboundStage {
  return (OUTBOUND_STAGES as readonly string[]).includes(stage);
}

function isInboundStage(stage: string): stage is InboundStage {
  return (INBOUND_STAGES as readonly string[]).includes(stage);
}

/** PipelineMini only knows the outbound stages, so inbound rows show their stage label. */
export function StageCell({ row }: { row: PortalDocumentRow }) {
  if (row.direction === "in") {
    return <span>{isInboundStage(row.stage) ? IN_STAGE_LABEL[row.stage] : row.stage}</span>;
  }
  const index = isOutboundStage(row.stage) ? stageIndex(row.stage) : 0;
  return (
    <span className="row-tight">
      {row.state === "error" ? <PipelineMini index={index} state="failed" /> : <PipelineMini index={index} />}
      <span className={styles.stageText}>{stageLabel(row.stage, row.direction)}</span>
    </span>
  );
}

export function DocumentCells({ row }: { row: PortalDocumentRow }) {
  const amount = row.totalAmount === null ? null : Number(row.totalAmount);
  return (
    <>
      <td>
        <Link className="link" to={`/portal/documents/${row.id}`}>
          {documentName(row)}
        </Link>
        {row.uuid ? <div className={`mono ${styles.uuid}`}>{row.uuid}</div> : null}
      </td>
      <td className="nowrap">
        <DocTypeCell typeCode={row.typeCode} flags={row.flags} direction={row.direction} />
      </td>
      <td>{row.counterparty ?? "—"}</td>
      <td className="nowrap">
        <StageCell row={row} />
      </td>
      <td className="nowrap">
        <StatusPill {...statePill(row.state)} />
        {row.state === "error" && row.errorCode ? (
          <div className={`mono ${styles.errorCode}`}>{row.errorCode}</div>
        ) : null}
      </td>
      <td className="r nowrap">{`${omr(amount)}${row.currency ? ` ${row.currency}` : ""}`}</td>
      <td className="nowrap">{relative(row.updatedAt)}</td>
    </>
  );
}

/** The console queue minus the company filter: the session pins the company. */
export function PortalDocuments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readQueueFilters(searchParams), [searchParams]);

  const query = useMemo<PortalDocumentFilters>(
    () => ({
      direction: filters.direction,
      stage: filters.stage.join(","),
      state: filters.state,
      from: filters.from,
      to: filters.to,
      q: filters.q,
      family: filters.family,
      typeCode: filters.typeCode.join(","),
      limit: PAGE_SIZE,
    }),
    [filters],
  );

  const list = usePortalDocuments(query);
  const counts = usePortalStageCounts({ direction: filters.direction });

  const patch = useCallback(
    (change: Partial<typeof filters>) => {
      setSearchParams(writeQueueFilters(mergeQueueFilters(filters, change)), { replace: true });
    },
    [filters, setSearchParams],
  );

  const clear = useCallback(() => setSearchParams(new URLSearchParams(), { replace: true }), [setSearchParams]);

  const rows = useMemo(() => (list.data?.pages ?? []).flatMap((page) => page.items), [list.data]);
  const filtered = isQueueFiltered(filters);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">Documents</h1>
          <p className={styles.lede}>Every document your company has sent and received.</p>
        </div>
      </div>

      <section className="card card-tight">
        <div className="card-head wrap">
          <div className={styles.filters}>
            <FilterControls
              filters={filters}
              onChange={patch}
              onClear={clear}
              hasFilters={filtered}
              stageCounts={counts.data}
            />
          </div>
        </div>

        {list.isError ? (
          <div className="card-body">
            <Notice kind="fail">The document list could not be loaded.</Notice>
          </div>
        ) : rows.length === 0 && !list.isLoading ? (
          <div className="card-body">
            <Notice kind="info">
              {filtered ? (
                <div className="row">
                  <span className="grow">No documents match these filters</span>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={clear}>
                    Clear filters
                  </button>
                </div>
              ) : (
                "No documents yet for your company"
              )}
            </Notice>
          </div>
        ) : (
          <>
            <div className={`table-wrap ${styles.tableScroll}`}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Counterparty</th>
                    <th>Stage</th>
                    <th>Status</th>
                    <th className="r">Value</th>
                    <th className="nowrap">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <DocumentCells row={row} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage ? (
              <div className="card-body">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>
    </>
  );
}
